import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { retryOperation } from "../utils/requests";
import { getUserOpenCashier } from "../utils/cashier";
import { checkDeliveryCenterRegister } from "../utils/deliveryCenter";
import { getDeliveryCenterConfig } from "../api/DeliveryCenter/deliveryCenter";
import MerchantsService from "../api/merchant";
import { dispatchSnackBarMessage } from "../utils/snackbar";
import { setCenter } from "../store/actions/mapSlice";
import { setShowNewCashierModal } from "../store/actions/cashierSlice";
import {
  setMerchantIfoodIds,
  setShowMerchantSetupModal,
} from "../store/actions/merchantsSlice";
import { setShowOnboardingModal } from "../store/actions/onboardingSlice";
import { updateOrders } from "../store/actions/ordersSlice";
import { hasCompletedOnboarding } from "../utils/onboarding";

const useDashboardData = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useSelector((state) => state.auth.user);
  const deliveryCenter = useSelector(
    (state) => state.deliveryCenter.deliveryCenter
  );
  const mapCenter = useSelector((state) => state.map.center);

  const loadConfig = async () => {
    try {
      const params = {
        config: "ifood_chat_merchant_ids",
      };
      const {
        data: { ifood_chat_merchant_ids },
      } = await getDeliveryCenterConfig(params);

      dispatch(setMerchantIfoodIds(ifood_chat_merchant_ids));
    } catch (err) {
      console.error(err);
      dispatchSnackBarMessage(
        "config-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    }
  };

  const checkMerchants = async () => {
    try {
      if (!user) {
        return;
      }

      const { data } = await MerchantsService.getMerchants();

      if (!data || data.length === 0) {
        dispatch(setShowMerchantSetupModal(true));
      }
    } catch (err) {
      console.error("Error checking merchants:", err);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      if (!user) {
        return;
      }

      // Check if both delivery center and merchants exist first
      const { data: merchants } = await MerchantsService.getMerchants();

      if (!deliveryCenter || !merchants || merchants.length === 0) {
        return; // Prerequisites not met
      }

      // Check API config for completion status
      const isCompleted = await hasCompletedOnboarding();

      if (isCompleted) {
        return; // Already completed
      }

      // All conditions met and not completed - show onboarding
      dispatch(setShowOnboardingModal(true));
    } catch (err) {
      console.error("Error checking onboarding:", err);
    }
  };

  const loadData = useCallback(async () => {
    try {
      if (!user) {
        return;
      }

      const deliveryCenter = await checkDeliveryCenterRegister(history);
      if (!deliveryCenter) {
        return;
      }

      const cashier = await getUserOpenCashier();
      if (cashier === false && user.user_type !== 0) {
        dispatch(setShowNewCashierModal(true));
      }

      // Check merchants after delivery center and cashier setup
      await checkMerchants();

      // Check onboarding status after all setup checks
      await checkOnboardingStatus();
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        "config-load-error",
        "Erro ao carregar dados",
        "error"
      );
    }
  }, [dispatch, history, user]);

  const loadDeliveryCenter = async () => {
    try {
      await checkDeliveryCenterRegister(history);
    } catch (e) {
      console.error(e);
    }
  };

  const updateMapCenter = () => {
    if (deliveryCenter && (mapCenter.lat === 0 || mapCenter.lng === 0)) {
      dispatch(
        setCenter({
          lat: parseFloat(deliveryCenter.lat),
          lng: parseFloat(deliveryCenter.lng),
        })
      );
    }
  };

  const fetchInitialData = async () => {
    try {
      await retryOperation(loadData, 3, 1000);
    } catch (error) {
      console.error("Failed to load data:", error);
    }
  };

  useEffect(() => {
    dispatch(
      updateOrders({ newSearch: true, includeNeighborhoodGroups: true })
    );
    loadConfig();
  }, [dispatch]);

  useEffect(() => {
    if (!deliveryCenter?.center_id) {
      loadDeliveryCenter();
    }
    updateMapCenter();
  }, [deliveryCenter, mapCenter.lat, mapCenter.lng, history, dispatch]);

  useEffect(() => {
    fetchInitialData();
  }, [loadData, user]);

  return {
    loadConfig,
    loadData,
    fetchInitialData,
  };
};

export default useDashboardData;
